import { AsyncPipe } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserContentItem } from '@polaris-customerweb/sharedcomponents.user-content';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { assign, attempt } from 'lodash-es';
import { Observable } from 'rxjs';
import { CommunityPolicyDialogComponent } from 'src/app/components/policies/community-policy/community-policy-dialog/community-policy-dialog.component';
import { ConfirmDialogOptions } from 'src/app/components/shared/components/confirm-dialog/confirm-dialog-options';
import { ConfirmDialogComponent } from 'src/app/components/shared/components/confirm-dialog/confirm-dialog.component';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { Collection } from 'src/app/models/collection';
import { CommunityItem } from 'src/app/models/community-item';
import { Tag } from 'src/app/models/tag';
import { CommunityItemService } from 'src/app/services/community-item.service';
import { TagService } from 'src/app/services/tag.service';
import { ActivityCardComponent } from '../../../community/shared-community/activity-card/activity-card.component';
import { ShareToCommunityDialogOptions } from './share-to-community-dialog-options';

@Component({
    imports: [
        ActivityCardComponent,
        AnalyticsDataDirective,
        AnalyticsDirective,
        AngularSvgIconModule,
        AsyncPipe,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
    ],
    selector: 'rdx-share-to-community-dialog',
    standalone: true,
    styleUrls: ['./share-to-community-dialog.component.scss'],
    templateUrl: './share-to-community-dialog.component.html',
})
export class ShareToCommunityDialogComponent implements OnDestroy, OnInit {
    @Input() userContentItem: UserContentItem = new Collection();
    @Input() communityItem: CommunityItem = new CommunityItem();

    certify: boolean;
    waypointTags$: Observable<Tag[]>;
    isSubmitting = false;

    private destructors: (() => void)[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA) data: ShareToCommunityDialogOptions,
        private communityItemService: CommunityItemService,
        private tagService: TagService,
        private matDialog: MatDialog,
        private matDialogRef: MatDialogRef<CommunityPolicyDialogComponent>,
        private matSnackBar: MatSnackBar
    ) {
        assign(this, data);
    }

    ngOnInit(): void {
        if (!this.communityItem.id && this.userContentItem) {
            this.communityItem = CommunityItem.fromUserContentItem(
                this.userContentItem
            );
        }
        this.waypointTags$ = this.tagService.getTags('userwaypoints-initial');
    }

    ngOnDestroy(): void {
        this.destructors.forEach(attempt);
    }

    onCancel(): void {
        this.matDialogRef.close();
    }

    onPost(): void {
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;

        const upsertCommunityItem$ = this.communityItem.id
            ? this.communityItemService.updateCommunityItem(this.communityItem)
            : this.communityItemService.createCommunityItem(this.communityItem);

        upsertCommunityItem$.subscribe({
            error: (error) => {
                this.isSubmitting = false;

                if (error.status === 422) {
                    const data = new ConfirmDialogOptions({
                        confirmButtonId: 'profanityConfirmation',
                        confirmText: $localize`OK`,
                        contentText: `Community items must not contain profanity in name and description.`,
                        dialogId: 'profanityErrorDialog',
                        hideCancelButton: true,
                        titleText: $localize`Error`,
                    });
                    this.matDialog.open(ConfirmDialogComponent, { data });
                } else {
                    this.matSnackBar.open(
                        $localize`Unknown Error has occurred, please try again later.`
                    );
                }
            },
            next: (communityItem) => {
                this.matSnackBar.open($localize`Successfully shared!`);
                this.matDialogRef.close(communityItem);
            },
        });
    }

    openCommunityPolicy(): void {
        const dialogRef = this.matDialog.open(CommunityPolicyDialogComponent);

        const dialogSub = dialogRef.afterClosed().subscribe();
        this.destructors.push(() => dialogSub.unsubscribe.bind(this));
    }
}
